import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, Chip, CircularProgress, IconButton, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Autocomplete,
  TextField, Avatar
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NotesIcon from '@mui/icons-material/Notes';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import PendingIcon from '@mui/icons-material/Pending';
import HistoryIcon from '@mui/icons-material/History';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { getWeekDays, formatDayLabel, formatWeekRange } from '../utils/dateUtils';
import RejectTimecardDialog from './RejectTimecardDialog';
import FixedSnackbar from './FixedSnackbar';

export default function SupervisorTimecardReview() {
  const { t, lang } = useTranslation();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedTc, setSelectedTc] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogText, setNoteDialogText] = useState('');
  const [noteDialogDay, setNoteDialogDay] = useState('');
  const [filterEmail, setFilterEmail] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pendRes, histRes] = await Promise.all([
        fetch(API_ENDPOINTS.TIMECARD_PENDING, { credentials: 'include' }),
        fetch(API_ENDPOINTS.TIMECARD_HISTORY, { credentials: 'include' })
      ]);
      if (pendRes.ok) {
        const data = await pendRes.json();
        setPending(data.timecards || []);
      }
      if (histRes.ok) {
        const data = await histRes.json();
        setHistory(data.timecards || []);
      }
    } catch (err) {
      console.error('Error fetching timecards:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    [...pending, ...history].forEach((tc) => {
      if (!map.has(tc.user_email)) {
        map.set(tc.user_email, { email: tc.user_email, fullname: tc.fullname || tc.user_email });
      }
    });
    return Array.from(map.values());
  }, [pending, history]);

  const filteredPending = useMemo(() => {
    if (!filterEmail) return pending;
    return pending.filter((tc) => tc.user_email === filterEmail);
  }, [pending, filterEmail]);

  const filteredHistory = useMemo(() => {
    if (!filterEmail) return history;
    return history.filter((tc) => tc.user_email === filterEmail);
  }, [history, filterEmail]);

  const handleApprove = async (tc) => {
    setSelectedTc(tc);
    setApproveDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedTc) return;
    setActionLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.TIMECARD_APPROVE(selectedTc.user_email, selectedTc.week_start), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_supervisor_error_approve');
      }
      setSnackbar({ open: true, message: t('calendar_supervisor_approved'), severity: 'success' });
      setApproveDialogOpen(false);
      setSelectedTc(null);
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_supervisor_error_approve'), severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (reason) => {
    if (!selectedTc) return;
    setActionLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.TIMECARD_REJECT(selectedTc.user_email, selectedTc.week_start), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'calendar_supervisor_error_reject');
      }
      setSnackbar({ open: true, message: t('calendar_supervisor_rejected'), severity: 'success' });
      setRejectDialogOpen(false);
      setSelectedTc(null);
      fetchAll();
    } catch (err) {
      setSnackbar({ open: true, message: t(err.message) || t('calendar_supervisor_error_reject'), severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const expandKey = (tc) => `${tc.user_email}-${tc.week_start}`;

  const DayChips = ({ tc }) => (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {getWeekDays(tc.week_start).map((d) => {
        const dayData = (tc.days || {})[d] || {};
        const hasNotes = Boolean(dayData.notes);
        return (
          <Chip
            key={d}
            icon={hasNotes ? (
              <Tooltip title={dayData.notes}>
                <NotesIcon
                  fontSize="small"
                  sx={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setNoteDialogDay(formatDayLabel(d, lang));
                    setNoteDialogText(dayData.notes);
                    setNoteDialogOpen(true);
                  }}
                />
              </Tooltip>
            ) : undefined}
            label={`${formatDayLabel(d, lang)}: ${dayData.hours || 0}h`}
            variant="outlined"
            size="small"
            sx={{
              height: 28,
              fontWeight: 500,
              ...(hasNotes ? {
                borderColor: 'primary.main',
                bgcolor: 'rgba(25,118,210,0.04)'
              } : {})
            }}
          />
        );
      })}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading timecards...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Employee Filter */}
      <Autocomplete
        options={employeeOptions}
        getOptionLabel={(opt) => opt.fullname || opt.email}
        value={filterEmail ? employeeOptions.find((o) => o.email === filterEmail) || null : null}
        onChange={(_, val) => setFilterEmail(val ? val.email : null)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('calendar_supervisor_filter_employee')}
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 3, minWidth: { xs: '100%', sm: 200 }, maxWidth: 350 }}
        clearOnEscape
      />

      {/* Pending Review Section */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
            <PendingIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calendar_supervisor_pending_section')}
          </Typography>
          {filteredPending.length > 0 && (
            <Chip label={filteredPending.length} size="small" sx={{ height: 24, fontWeight: 500 }} />
          )}
        </Box>

        {filteredPending.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('calendar_supervisor_no_pending')}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('name') || 'Name'}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{lang === 'spanish' ? 'Semana' : 'Week'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('calendar_total_hours')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('calendar_notes_label')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPending.map((tc) => (
                  <React.Fragment key={expandKey(tc)}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1.5 } }}
                      onClick={() => setExpanded(expanded === expandKey(tc) ? null : expandKey(tc))}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{tc.fullname || tc.user_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatWeekRange(tc.week_start, lang)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{tc.total_hours}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small">
                          {expanded === expandKey(tc) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          startIcon={actionLoading ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                          onClick={(e) => { e.stopPropagation(); handleApprove(tc); }}
                          disabled={actionLoading}
                          sx={{ mr: 1, textTransform: 'none', boxShadow: 'none' }}
                        >
                          {t('calendar_supervisor_approve')}
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={(e) => { e.stopPropagation(); setSelectedTc(tc); setRejectDialogOpen(true); }}
                          disabled={actionLoading}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('calendar_supervisor_reject')}
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expanded === expandKey(tc)}>
                          <Box sx={{ py: 2, px: 1 }}>
                            <DayChips tc={tc} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* History Section */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.100', color: 'grey.600' }}>
            <HistoryIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calendar_supervisor_history_section')}
          </Typography>
          {filteredHistory.length > 0 && (
            <Chip label={filteredHistory.length} size="small" sx={{ height: 24, fontWeight: 500 }} />
          )}
        </Box>

        {filteredHistory.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('calendar_supervisor_no_history')}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('name') || 'Name'}</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{lang === 'spanish' ? 'Semana' : 'Week'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('calendar_total_hours')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('status')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>{t('calendar_notes_label')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHistory.map((tc) => {
                  const statusColor = { approved: 'success', rejected: 'error' };
                  return (
                    <React.Fragment key={expandKey(tc)}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer', '& .MuiTableCell-root': { py: 1.5 } }}
                        onClick={() => setExpanded(expanded === expandKey(tc) ? null : expandKey(tc))}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{tc.fullname || tc.user_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatWeekRange(tc.week_start, lang)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{tc.total_hours}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={t(`calendar_status_${tc.status}`)}
                            size="small"
                            color={statusColor[tc.status] || 'default'}
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small">
                            {expanded === expandKey(tc) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                          <Collapse in={expanded === expandKey(tc)}>
                            <Box sx={{ py: 2, px: 1 }}>
                              {tc.rejection_reason && (
                                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                                  {t('calendar_rejection_reason')}: {tc.rejection_reason}
                                </Typography>
                              )}
                              <DayChips tc={tc} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Note View Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('calendar_notes_label')}
            </Typography>
            {noteDialogDay && (
              <Typography variant="caption" color="text.secondary">
                {noteDialogDay}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setNoteDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {noteDialogText}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setNoteDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      <RejectTimecardDialog
        open={rejectDialogOpen}
        onClose={() => { setRejectDialogOpen(false); setSelectedTc(null); }}
        onConfirm={handleReject}
      />

      {/* Approve Confirmation Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => { setApproveDialogOpen(false); setSelectedTc(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('calendar_supervisor_approve_confirm') || 'Are you sure you want to approve this timecard?'}
          </Typography>
          <IconButton onClick={() => { setApproveDialogOpen(false); setSelectedTc(null); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedTc && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedTc.fullname || selectedTc.user_email} — {formatWeekRange(selectedTc.week_start, lang)}
            </Typography>
          )}
          {selectedTc && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('calendar_total_hours')}: {selectedTc.total_hours}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => { setApproveDialogOpen(false); setSelectedTc(null); }} sx={{ textTransform: 'none' }}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={confirmApprove}
            disabled={actionLoading}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {actionLoading ? <CircularProgress size={18} /> : t('calendar_supervisor_approve')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
        autoHideDuration={4000}
      />
    </Box>
  );
}
